import { User } from '@biz-nest/auth-client';
import { IcommerceUser, Iuser, makeRandomString } from '@biz-nest/core-client';
import { Imanager } from '../../shared-types';

/**
 * Extracts a user ID from either a User object or a string ID.
 * @param {User | string} user - The user object or string ID to extract from
 * @returns {string} The user ID if found, otherwise an empty string
 * @example
 * const userId = determineIdFn(userObject); // returns '12345'
 * const userId2 = determineIdFn('12345'); // returns '12345'
 */
export const determineIdFn = (user: User | string): string => (user as User)?.urId || (user as string);

/**
 * Generates a random number within specified limits (inclusive).
 * @param {number} lowerLimit - The lower bound (inclusive)
 * @param {number} upperLimit - The upper bound (inclusive)
 * @returns {number} A random number between lowerLimit and upperLimit
 * @throws {Error} If lowerLimit is greater than upperLimit
 * @example
 * const randomNum = makeRandomNoBtnLimits(1, 10); // returns a number between 1 and 10
 */
export const makeRandomNoBtnLimits = (lowerLimit: number, upperLimit: number) => {
  if (lowerLimit > upperLimit) {
    throw new Error('lowerLimit must be less than or equal to upperLimit');
  }

  let rand = 0;

  for (let i = 0; i < Infinity; i++) {
    rand = Number(makeRandomString(1, 'numbers'));
    if (rand >= lowerLimit && rand <= upperLimit) {
      break;
    }
  }

  return rand;
};

/**
 * Converts a date to a string in 'YYYY-MM-DD' format.
 * @param {Date | string | number} date - The date to format
 * @returns {string} Formatted date string
 * @example
 * const dateStr = transformDateWithoutTime(new Date()); // '2023-05-15'
 */
export const transformDateWithoutTime = (date: Date | string | number): string => {
  const newDate = new Date(date);
  const year = newDate.getFullYear();
  const month = newDate.getMonth() + 1;
  const day = newDate.getDate();

  return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
};

/**
 * Generates a random password with 12 characters.
 * Excludes easily confusable characters like '1', 'l', 'I', etc.
 * @returns {string} A randomly generated password
 * @example
 * const password = generateRandomPassword(); // 'aB3dE4fG5hI6'
 */
export const generateRandomPassword = (): string => {
  let pw = '';
  const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

  for (let i = 0; i < 12; i++) {
    pw += charset.charAt(Math.floor(Math.random() * charset.length));
  }

  return pw;
};

/**
 * Creates an array of 1's with specified length.
 * @param {number} length - The desired length of the array
 * @returns {number[]} An array filled with 1's
 * @example
 * const arr = makeArrayOfLength(3); // returns [1, 1, 1]
 */
export const makeArrayOfLength = (length: number): number[] => {
  return Array(length).fill(1);
};

/**
 * Validates an email address format.
 * @param {string} email - The email address to validate
 * @returns {boolean} True if the email format is valid, false otherwise
 * @example
 * const isValid = validateEmail('test@example.com'); // returns true
 */
export const validateEmail = (email: string): boolean => {
  // eslint-disable-next-line max-len
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return re.test(String(email).toLowerCase());
};

/**
 * Combines first and last name from a user object.
 * @param {Iuser | IcommerceUser | Imanager | string} data - The user data
 * @returns {string} Combined first and last name, or empty string if input is a string
 * @example
 * const fullName = getCompositionUserNames({ firstName: 'John', lastName: 'Doe' }); // 'John Doe'
 */
export const getCompositionUserNames = (data: Iuser | IcommerceUser | Imanager | string): string => {
  return typeof data === 'string' ? '' : `${data?.firstName || ''} ${data?.lastName || ''}`.trim();
};